// Rich Solar inventory checking functionality
let richSolarDataCache = null;
let productVariantsCache = null; // Cache the variants to avoid reparsing
let currentBackorderMap = null; // Store current backorder data for variant changes

// Check if any variants have RS- prefix SKUs
function hasRichSolarVariants() {
  const variants = getProductVariants();
  const hasRS = variants?.some(variant => variant.sku?.startsWith('RS-')) || false;
  return hasRS;
}

// Get product variants from the JSON script tag (with caching)
function getProductVariants() {
  if (productVariantsCache) {
    return productVariantsCache;
  }
  
  // Try multiple ways to find product variants data
  const variantSelectsElement = document.querySelector('variant-selects');
  if (variantSelectsElement) {
    const scriptTag = variantSelectsElement.querySelector('script[type="application/json"]');
    if (scriptTag) {
      try {
        productVariantsCache = JSON.parse(scriptTag.textContent);
        return productVariantsCache;
      } catch (e) {
        console.log('❌ Error parsing variants JSON');
      }
    }
  }
  
  // For products without variant-selects (single variant products)
  // Try to get SKU directly from the page
  const skuElement = document.querySelector('.variant-sku');
  if (skuElement) {
    const skuText = skuElement.textContent.replace('SKU:', '').trim();
    if (skuText) {
      // Create a single-variant array to maintain consistency
      productVariantsCache = [{
        id: skuElement.dataset.variantId || 'single-variant',
        sku: skuText,
        option1: null,
        option2: null,
        option3: null,
        available: true // Will be checked against Rich Solar inventory
      }];
      return productVariantsCache;
    }
  }
  
  console.log('❌ No variants data found');
  return null;
}

// Fetch Rich Solar inventory data
async function fetchRichSolarInventory() {
  if (richSolarDataCache) {
    return richSolarDataCache;
  }

  try {
    const response = await fetch('https://richsolar.com/products.json?page=1&limit=250');
    if (!response.ok) throw new Error('Failed to fetch Rich Solar inventory');
    
    const data = await response.json();
    
    // Create a map of SKU to availability for quick lookup
    const inventoryMap = new Map();
    const backorderMap = new Map();
    data.products?.forEach(product => {
      const isBackorder = product.title?.toLowerCase().includes('backorder') || product.body_html?.toLowerCase().includes('backorder');
      product.variants?.forEach(variant => {
        if (variant.sku) {
          inventoryMap.set(variant.sku, variant.available);
          if (isBackorder) {
            backorderMap.set(variant.sku, true);
          }
        }
      });
    });
    
    richSolarDataCache = { inventoryMap, backorderMap };
    return richSolarDataCache;
  } catch (error) {
    console.error('Error fetching Rich Solar inventory:', error);
    return { inventoryMap: new Map(), backorderMap: new Map() };
  }
}

// Check if a variant is available in Rich Solar inventory
function isVariantAvailableInRichSolar(sku, inventoryMap) {
  if (!sku || !sku.startsWith('RS-')) {
    return true; // Non-RS products are always considered available
  }
  
  return inventoryMap.get(sku) === true;
}

// Check if any RS- variants are on backorder
function hasBackorderVariants(backorderMap) {
  const variants = getProductVariants();
  return variants?.some(variant => variant.sku?.startsWith('RS-') && backorderMap.get(variant.sku)) || false;
}

// Get the currently selected variant SKU
function getCurrentSku() {
  // First try to find the visible SKU element (for multi-variant products)
  const visibleSkuElement = document.querySelector('.variant-sku:not([style*="display: none"])');
  if (visibleSkuElement) {
    return visibleSkuElement.textContent.replace('SKU:', '').trim();
  }
  
  // Fallback to any SKU element (for single-variant products)
  const anySkuElement = document.querySelector('.variant-sku');
  if (anySkuElement) {
    return anySkuElement.textContent.replace('SKU:', '').trim();
  }
  
  return null;
}

// Update backorder notice based on current variant
function updateBackorderNotice() {
  if (!currentBackorderMap) return;
  
  const currentSku = getCurrentSku();
  const startsWithRS = currentSku && currentSku.startsWith('RS-');
  const isOnBackorder = currentBackorderMap.has(currentSku);
  const shouldShow = currentSku && startsWithRS && isOnBackorder;
  
  addBackorderNotice(shouldShow);
}

// Disable variant options that are out of stock according to Rich Solar
function disableUnavailableVariants(inventoryMap) {
  const variants = getProductVariants();
  if (!variants) return;
  
  // Check if this is a single-variant product
  const isSingleVariant = variants.length === 1 && !document.querySelector('variant-selects');
  
  variants.forEach((variant, index) => {
    if (variant.sku && variant.sku.startsWith('RS-')) {
      
      const isAvailable = isVariantAvailableInRichSolar(variant.sku, inventoryMap);
      
      if (!isAvailable) {
        if (isSingleVariant) {
          // For single-variant products, disable the add to cart button
          disableSingleVariantProduct();
        } else {
          // Find and disable corresponding radio buttons/selects
          disableVariantControls(variant);
        }
      }
    }
  });
}

// Disable add to cart button for single-variant products
function disableSingleVariantProduct() {
  const addToCartButton = document.querySelector('product-form .product-form__submit, product-form button[type="submit"]');
  if (addToCartButton) {
    addToCartButton.disabled = true;
    
    // Update button text to show it's sold out
    const buttonText = addToCartButton.querySelector('.add-to-cart-text, span');
    if (buttonText) {
      buttonText.textContent = 'Sold Out';
    } else {
      addToCartButton.textContent = 'Sold Out';
    }
    
    addToCartButton.classList.add('disabled', 'sold-out');
  }
  
  // Also add a sold out notice if desired
  const productForm = document.querySelector('product-form');
  if (productForm && !document.querySelector('.rich-solar-sold-out-notice')) {
    const notice = document.createElement('div');
    notice.className = 'rich-solar-sold-out-notice';
    notice.style.cssText = 'color: #721c24; background-color: #f8d7da; border: 1px solid #f5c6cb; padding: 12px; border-radius: 4px; margin-bottom: 16px;';
    notice.innerHTML = '<strong>Out of Stock:</strong> This product is currently unavailable from the manufacturer.';
    productForm.insertAdjacentElement('beforebegin', notice);
  }
}

// Disable the actual form controls for an unavailable variant
function disableVariantControls(variant) {
  // Method 1: Find radio buttons in the product form by value matching
  const radioButtons = document.querySelectorAll('.product-form__input input[type="radio"]');
  radioButtons.forEach(radio => {
    if (isRadioForVariant(radio, variant)) {
      radio.disabled = true;
      
      // Add visual styling to the radio button and its label
      const label = radio.closest('label') || document.querySelector(`label[for="${radio.id}"]`);
      if (label) {
        label.classList.add('disabled', 'sold-out');
        label.style.opacity = '0.5';
        label.style.pointerEvents = 'none';
        
        // Add "- Sold Out" to the label text
        const labelSpan = label.querySelector('span');
        if (labelSpan && !labelSpan.textContent.includes('- Sold Out')) {
          labelSpan.textContent += ' - Sold Out';
        }
      }
      
      // Also disable the parent fieldset if needed
      const fieldset = radio.closest('.product-form__input');
      if (fieldset) {
        fieldset.classList.add('has-sold-out-variant');
      }
    }
  });
  
  // Method 2: Find select options
  const selectOptions = document.querySelectorAll('select option');
  selectOptions.forEach(option => {
    if (isOptionForVariant(option, variant)) {
      option.disabled = true;
      option.textContent += ' - Unavailable';
    }
  });
  
  // Method 3: Handle Shopify's variant picker buttons/swatches
  const variantButtons = document.querySelectorAll('[data-variant-id], .variant-button, .color-swatch');
  variantButtons.forEach(button => {
    if (button.dataset.variantId === variant.id.toString() || 
        isButtonForVariant(button, variant)) {
      button.disabled = true;
      button.classList.add('disabled', 'sold-out');
      button.style.pointerEvents = 'none';
      button.style.opacity = '0.5';
    }
  });
}

// Check if a radio button corresponds to a specific variant
function isRadioForVariant(radio, variant) {
  // This is tricky - we need to match radio button values to variant options
  // Shopify typically uses option values like "Black", "Large", etc.
  
  // Get the radio's value and see if it matches any of the variant's options
  const radioValue = radio.value;
  
  // Check against all variant options (option1, option2, option3)
  return radioValue === variant.option1 || 
         radioValue === variant.option2 || 
         radioValue === variant.option3;
}

// Check if a select option corresponds to a specific variant
function isOptionForVariant(option, variant) {
  const optionValue = option.value;
  
  // Similar logic to radio buttons
  return optionValue === variant.option1 || 
         optionValue === variant.option2 || 
         optionValue === variant.option3;
}

// Check if a button/swatch corresponds to a specific variant
function isButtonForVariant(button, variant) {
  // Check data attributes that might contain variant info
  const buttonValue = button.dataset.value || button.textContent?.trim();
  
  return buttonValue === variant.option1 || 
         buttonValue === variant.option2 || 
         buttonValue === variant.option3;
}

// Add or remove backorder notice based on current variant
function addBackorderNotice(show = true) {
  const existingNotice = document.querySelector('.backorder-notice');
  
  if (show && !existingNotice) {
    // Add the notice
    const noticeHtml = `
      <div class="drop-ship-notice backorder-notice" style="margin-bottom: 20px;">
        <div class="drop-ship-notice--icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
            <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="drop-ship-notice--content">
          <p><strong>Backorder Notice:</strong> This product is currently on backorder and may take longer to ship.</p>
        </div>
      </div>
    `;

    const container = document.querySelector('.product-title-container');
    if (container) {
      container.insertAdjacentHTML('afterend', noticeHtml);
    }
  } else if (!show && existingNotice) {
    // Remove the notice
    existingNotice.remove();
  }
}

// Initialize Rich Solar inventory checking
async function initRichSolarInventoryCheck() {
  const hasRS = hasRichSolarVariants();
  if (!hasRS) {
    return;
  }

  // Fetch inventory data
  const { inventoryMap, backorderMap } = await fetchRichSolarInventory();
  currentBackorderMap = backorderMap;
  
  // Disable unavailable variants upfront
  disableUnavailableVariants(inventoryMap);

  // Update backorder notice for initial variant
  updateBackorderNotice();

  // Listen for variant changes using MutationObserver (for multi-variant products)
  const skuElements = document.querySelectorAll('.variant-sku');
  if (skuElements.length > 1) {
    // Multiple SKU elements means multi-variant product
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          updateBackorderNotice();
        }
      });
    });
    
    skuElements.forEach(element => {
      observer.observe(element, { attributes: true, attributeFilter: ['style'] });
    });
  }

  // Also try the variant:change event as backup
  document.addEventListener('variant:change', updateBackorderNotice);
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  initRichSolarInventoryCheck();
});